import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { RootState } from '../app/store'
import { TopBar } from '@/components/home'
import PaymentMethodModal from '../components/payment/PaymentMethodModal'

import LoadingSpinner from '../components/LoadingSpinner'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { usePartialPayment } from '../hooks/usePartialPayment'
import OrderSummary from '../components/partialPayment/OrderSummary'
import RemainingAmount from '../components/partialPayment/RemainingAmount'
import PaymentHistory from '../components/partialPayment/PaymentHistory'
import PaymentInput from '../components/partialPayment/PaymentInput'
import { isCustomerServiceUser } from '../utils/apiUtils'

const PartialPaymentPage: React.FC = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  
  // Ensure i18n language matches settings
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language)
    }
  }, [settings.language, i18n])

  const {
    order,
    isLoading,
    error,
    paymentAmount,
    selectedPaymentMethod,
    isSubmitting,
    paymentHistory,
    remainingAmount,
    isLastPayment,
    currentPaymentCount,
    setPaymentAmount,
    setSelectedPaymentMethod,
    handleNumberPress,
    handleDeletePress,
    handleSelectPaymentMethod,
    handleSubmitPayment,
  } = usePartialPayment()

  const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false)
  const [isWosoolModalOpen, setIsWosoolModalOpen] = useState(false)
  const paymentHistoryRef = useRef<HTMLDivElement>(null)
  const previousPaymentCountRef = useRef<number>(0)

  const isRTL = settings.language === 'ar'

  // Auto-open payment method modal for last payment if no method is selected
  useEffect(() => {
    if (isLastPayment && !selectedPaymentMethod && !isLoading && order) {
      setIsPaymentMethodModalOpen(true)
    }
  }, [isLastPayment, selectedPaymentMethod, isLoading, order])

  // Auto-scroll to payment history when a new payment is added
  useEffect(() => {
    if (paymentHistory.length > previousPaymentCountRef.current && paymentHistoryRef.current) {
      // Wait a bit for the DOM to update
      setTimeout(() => {
        const element = paymentHistoryRef.current
        if (element) {
          const elementPosition = element.getBoundingClientRect().top
          const offsetPosition = elementPosition + window.pageYOffset - 100 // 100px offset from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          })
        }
      }, 300)
    }
    previousPaymentCountRef.current = paymentHistory.length
  }, [paymentHistory.length])

  // Handle payment method selection
  const handleSelectPaymentMethodWrapper = (method: 'cash' | 'network') => {
    if (method === 'network') {
      // Check if payment machine is connected (for non-CS users)
      if (!isCustomerServiceUser()) {
        toast.error(t('payment.connect_payment_machine'), {
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '16px',
            fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
            direction: isRTL ? 'rtl' : 'ltr',
          },
        })
        setIsPaymentMethodModalOpen(false)
        return
      }
      setSelectedPaymentMethod('network')
      setIsWosoolModalOpen(true)
    } else if (method === 'cash') {
      setSelectedPaymentMethod('cash')
    }
    setIsPaymentMethodModalOpen(false)
  }

  // Handle Wosool payment success
  const handleWosoolPaymentSuccess = (transactionId: string) => {
    setIsWosoolModalOpen(false)
    // Proceed with payment submission
    handleSubmitPayment('network', transactionId)
  }

  // Handle number press with payment method check
  const handleNumberPressWrapper = (number: string) => {
    if (!selectedPaymentMethod) {
      setIsPaymentMethodModalOpen(true)
      return
    }
    handleNumberPress(number)
  }

  // Handle pay button click
  const handlePayClick = () => {
    // Check if payment method is selected
    if (!selectedPaymentMethod) {
      setIsPaymentMethodModalOpen(true)
      return
    }

    // Check if amount is valid
    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      // This will be handled by handleSubmitPayment, but we can show a better message
      handleSubmitPayment(selectedPaymentMethod)
      return
    }

    // For network payment, check if payment machine is connected (for non-CS users)
    if (selectedPaymentMethod === 'network') {
      if (!isCustomerServiceUser()) {
        toast.error(t('payment.connect_payment_machine'), {
          duration: 5000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '16px',
            fontFamily: isRTL ? 'IBM Plex Sans Arabic, sans-serif' : 'inherit',
            direction: isRTL ? 'rtl' : 'ltr',
          },
        })
        return
      }
      // Open Wosool modal if not already open
      if (!isWosoolModalOpen) {
        setIsWosoolModalOpen(true)
        return
      }
    }

    // Proceed with payment
    handleSubmitPayment(selectedPaymentMethod)
  }

  // Get payment method display name
  const getPaymentMethodName = (method: 'cash' | 'network'): string => {
    return method === 'network' ? t('payment.network') : t('payment.cash')
  }

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || t('orders.order_not_found')}</p>
          <button
            onClick={() => navigate('/orders')}
            className="px-4 py-2 bg-brand-base text-white rounded-lg"
          >
            {t('orders.back_to_orders')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-screen bg-neutral-100 flex flex-col overflow-hidden">
         
           <div className={` pt-2 flex-shrink-0 ${isRTL ? 'pr-0 pl-4' : 'pl-0 pr-4'}`}>
           <TopBar userName={user?.name} />
           </div>

      {/* Scrollable Content */}
      <div className="flex-1 px-6 pt-2 pb-4 overflow-y-auto min-h-0">
        {/* Top Bar */}

        <div className=" pb-6  items-start gap-8">
          {/* Header with title and back button */}
          <div className=" flex flex-row justify-start items-center gap-3">
          <button
              onClick={() => navigate(-1)}
              className="size-10 bg-white rounded-xl flex justify-center items-center gap-2"
            >
             {isRTL ? <ArrowRight className="size-5 text-icon-primary" /> : <ArrowLeft className="size-5 text-icon-primary" />}
            </button>
          
            <div className="  text-text-primary text-2xl font-medium font-['IBM_Plex_Sans_Arabic'] leading-8">
              {t('payment.partial_payment')}
            </div>

            
          
          </div>

          <div className="w-full flex flex-row justify-center items-start gap-5 flex-wrap lg:flex-nowrap max-[1100px]:flex-col max-[1100px]:items-center max-[1100px]:justify-center">
            {/* Payment Input - First on small screens */}
            <div className="flex w-full lg:w-auto max-[1100px]:w-full max-[1100px]:max-w-md max-[1100px]:flex max-[1100px]:justify-center">
              <PaymentInput
                paymentAmount={paymentAmount}
                selectedPaymentMethod={selectedPaymentMethod}
                remainingAmount={remainingAmount}
                isLastPayment={isLastPayment}
                currentPaymentCount={currentPaymentCount}
                isSubmitting={isSubmitting}
                onNumberPress={handleNumberPressWrapper}
                onDeletePress={handleDeletePress}
                onPaymentMethodClick={() => setIsPaymentMethodModalOpen(true)}
                onPayClick={handlePayClick}
                getPaymentMethodName={getPaymentMethodName}
              />
            </div>

            {/* Order Summary and Payment History - Second on small screens */}
            <div className="flex-shrink-0 w-full lg:w-[512px] max-w-full max-[1100px]:w-full max-[1100px]:max-w-md flex flex-col justify-start items-start gap-4 pr-2 max-[1100px]:items-center max-[1100px]:pr-0">
              {/* Order Summary Card - Smaller height on small screens */}
              <div className="w-full h-[450px] lg:h-[500px] max-[1200px]:h-[400px] flex-shrink-0">
                <OrderSummary order={order} isRTL={isRTL} />
              </div>

              {/* Remaining Amount Card */}
              <div className="w-full max-[1100px]:max-w-md">
                <RemainingAmount remainingAmount={remainingAmount} />
              </div>

              {/* Payment History Cards */}
              <div ref={paymentHistoryRef} className="w-full max-[1100px]:max-w-md">
                <PaymentHistory payments={paymentHistory} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={isPaymentMethodModalOpen}
        onClose={() => setIsPaymentMethodModalOpen(false)}
        showPartialPayment={false}
        onSelectPaymentMethod={handleSelectPaymentMethodWrapper}
      />

     
    </div>
  )
}

export default PartialPaymentPage
